/** @module csi/form/Widget */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/_base/fx",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/data/ItemFileReadStore",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-style",
	"dojo/on",
	"dojo/promise/all",
	"dojo/when",
	"dijit/focus",
	"dijit/tree/ForestStoreModel",
	"src/c8-platform/service/aggregateStoreService",
	"src/c8-platform/type/form",
	"csi",
	"csi/api",
	"csi/_base/underscore",
	"csi/_base/Deferred",
	"csi/action/GoToDialogController",
	"csi/comm",
	"csi/config/preferences",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/def/Action",
	"csi/def/Context",
	"csi/def/Field",
	"csi/enums",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/middleware/rollup/rollupService",
	"csi/service/writerService",
	"csi/store/_base",
	"csi/types",
	"csi/view/Widget",
	"csi/widget/Automation",
	"csi/widget/CheckboxTree",
	"csi/widget/CheckedMultiRefSelect",
	"csi/widget/ErrorText",
	"csi/widget/RefSelect",
	"csi/widget/ValidationTextBox",
	"csi/util",
	"csi/utility/coreUtility",
	"csi/utility/pathUtility",
	"csi/xml",
	"csi/xml/Writer",
	"./Tooltip",
	"dojo/text!csi/templates/form.html",
	"dojo/text!csi/templates/formPopup.html",

	// load default renderers for fields
	"csi/heading/Boolean",
	"csi/heading/Number",
	"csi/heading/Ref",
	"csi/heading/RefContainer",
	"csi/heading/String",
	"csi/heading/StringTextarea",
	"csi/heading/Time",
	"csi/heading/TimeDateTime",
	"csi/heading/TimeOnly",
	"csi/heading/Url",
	"csi/widget/InvalidMessage"
], function(array, declare, event, fx, lang, aspect, ItemFileReadStore, domClass, domConstruct, domStyle, on, all, when, focus, ForestStoreModel,
	{aggregateStoreResponseHasMultipleKeys}, {FORM_ACTION_COPY, FORM_ACTION_EDIT, FORM_ACTION_NEW},
	csi, api, _, Deferred, GoToDialogController, comm, configPreferences, ExtendedPath, def, Action, Context, Field, enums, csiHeading, html, locale,
	reflection, {evaluate}, {writeFormFields, writeFormParent, writeFormPaths}, csiStore, types, Widget,
	Automation, CheckboxTree, CheckedMultiRefSelect, ErrorText, RefSelect, ValidationTextBox, util, {isNullOrUndefined}, {pathCreate}, xml, Writer, FormTooltip, template, templatePopup) {

	var CSS_VALUE = "csiFormViewValue";
	var CSS_VALUE_INPUT = "csiFormViewValueInput";
	var SAVE_NEW_TYPES = ["ref", "enum", "enumlist", "double", "boolean"];
	var VALIDATION_MESSAGES = ["invalidMessage", "missingMessage", "promptMessage"];

	var CONTROLS_WITH_RENDERER = [
		def.FIELD_CHECKBOX,
		def.FIELD_NUMBER,
		def.FIELD_TEXT,
		def.FIELD_TEXTAREA,
		def.FIELD_TIME
	];

	html.registerDataAttribute("DATA_FORM_FIELD", "form-field");
	html.registerDataAttribute("DATA_FORM_UNIQUEID", "form-uniqueid");

	function _getWidgetParams(/*Context*/ctx, /*Field*/field, /*Map*/keyFieldMap) {
		const fieldKey = field.getKey();
		var useUniqueControlName = false;
		if (keyFieldMap.has(fieldKey) && keyFieldMap[fieldKey] != field) {
			// if key has already been used for a different field, use uniqueControlName
			useUniqueControlName = true;
		} else if (!keyFieldMap.has(fieldKey)) {
			// if key is unique so far, add to map
			keyFieldMap.set(fieldKey, field);
		}

		var params = Automation.params(ctx.defView.getHtmlKey(), "field", useUniqueControlName ? field.getUniqueControlName() : field.getKey());

		// set the name of the control to allow the value of the control to be submitted in the form
		params.name = field.idParameter || field.getControlName();

		if (field.widgetParams) {
			lang.mixin(params, field.widgetParams);
		}

		_.each(VALIDATION_MESSAGES, function(msg) {
			if (params[msg]) {
				params[msg] = locale.resource(params[msg]);
			}
		});
		return params;
	}

	/**
	 * test if this domNode contains combined fields
	 * @param {DOMNode} e
	 * @return {boolean}
	 */
	function _containsCombinedField(e) {
		return domClass.contains(e, "csi-form-view-row-combined");
	}

	/**
	 * test if this domNode contains metaData field
	 * @param {DOMNode} e
	 * @return {boolean}
	 */
	function _containsMetaDataField(e) {
		return domClass.contains(e, "csiFormViewValueExtra");
	}

	return declare(Widget, {
		templateString: template,

		// Resize is required on fields value change, because fields visibility might change and it'll change form size.
		// When form redraws, function freezePopupScrollNode(true) being called to prevent resize flicker. Scroll node will unfreeze
		// after form resize. When form being created all those things works fine automatically, but we set this option to "true" for the case of
		// "Save & New" button, when form being frozen and re-drawn, but resize will not happen without this option.
		bResizeOnChange: true,

		// connection points
		bodyNode: null,
		headerNode: null,
		scrollNode: null,

		// Map<C8NodeType, ExtendedPath>: map of combined form extended paths mapped by type
		combinedPathMap: null,

		// Boolean: write only visible fields
		bWriteVisible: true,

		// Boolean: use the default save & new behavior
		bDefaultSaveAndNew: true,

		/** @property {boolean} bSkipFocusFirstField - do we skip focusing the first field on the form? */
		bSkipFocusFirstField: false,

		// Timeout Handle: used internally to indicate that submit delay is active
		// Do not use, do not override
		// bSubmitDelay: null,

		/** @property {IForm} formInfo - configuration and context of form (for now in Dojo, just used for context) */
		formInfo: null,

		// Widget: class to use for tooltip widget
		tooltipClass: FormTooltip,

		/** @property {Object} rightWidgetClass - widget class to appear on right side of form */
		rightWidgetClass: null,

		// Properties of rightWidget
		rightWidgetProperties: null,

		// Object: map of field control names to default values
		_defaultValues: null,

		// Object: map of field control names to fixed default values
		_fixedValues: null,

		/** @property {string} _formNodeUrl - url to node containing updated values from form fields */
		_formNodeUrl: null,

		// String[]: list of urls of nodes created to handle form fields
		_formNodeURLs: null,

		// Object: map of field control names to expressions indicating if field should be hidden
		_hideExpressions: null,

		// Object for Automation IDs: Map - (String) -> (Field)
		_keyFieldMap: null,

		// Object: map of existing nodes to form nodes created to handle possibly temporary form data
		_mapSourceNodes: null,

		// Object: map of field names to field values to populate fields
		_persistFormValues: null,

		// Object: map of required attributes expressions - visibility above trumps required status
		_requiredExpressions: null,

		// DOMNode: temporary node for resetting form
		_resetBodyNode: null,

		// Object: map of expressions (or function) to calculate field values - reflection expression will override these
		_valueExpressions: null,

		// Boolean: tells if the action started this form was a NewFrom or not.
		bNewFrom: false,

		// Deferred[]: list of deferred fields
		_deferredList: null,

		disableSave: function(/*Boolean*/disabled) {
			html.setDisabled(this.getActionButton(this.getSourceController().saveResourceId), disabled);
		},

		/**
		 * Return true if current form is being combined with another form
		 * @return {boolean}
		 */
		isCombinedForm() {
			return !!this.combinedPathMap.size;
		},

		/**
		 * Return type of action that opened form
		 * @return {FormActionType}
		 */
		_getActionType() {
			if (this.isNew()) return FORM_ACTION_NEW;
			if (this.isEdit()) return FORM_ACTION_EDIT;
			if (this.isCopy() || this.isNewFrom()) return FORM_ACTION_COPY;
		},

		isNewFrom: function() {
			return this.bNewFrom;
		},

		isNew: function() {
			return !this.getViewNode();
		},

		isEdit: function() {
			return !!this.getViewNode() && (!this.defView.m_formData || !!this.getFormSourceNode());
		},

		isCopy: function() {
			return !!this.getViewNode() && !!this.defView.m_formData && !this.getFormSourceNode();
		},

		/**
		 * Return the field name in the combined Form
		 * @param {String} destinationClass Id to the secondary form to be combined with the current form
		 * @param {String} attributeName
		 * @return {String}  - example SpecificationItemDefinition/Node Name
		 */
		getCombinedFieldName: function(destinationClass, attributeName) {
			return destinationClass ? (destinationClass + "/" + attributeName) : attributeName;
		},

		/**
		 * Return fields from combined forms, optionally filtered by desinationClass if given
		 * @param {String} destinationClass combined form class to filter fields by
		 * @return {Field[]} - returns an array of fields.
		 */
		getCombinedFields: function(destinationClass) {
			const combinedFields = this.defView.m_fields.filter(f => (
				destinationClass ? f.combinedFormType === destinationClass : f.combinedFormType
			));
			return combinedFields;
		},

		/**
		 * This method is called as part of combineForm. If form for the path specified does not exist,
		 * it will call this method to get an array of form fields. If this method is called, it will behave
		 * like a hard-coded form.
		 * @param {String} destinationClass Id to the secondary form to be combined with the current form
		 * @return {Field[]} fields - returns an array of hard-coded fields.
		 */
		_getCustomSecondaryFormFields: function(destinationClass) {
			return null;
		},

		/**
		 * Combine the form from the destination class specified with the corresponding path
		 * @param {boolean} addToBeginning - If true, it will add the secondary form to the beginning of the current form
		 * @param {ExtendedPath|string} extendedPath - typed path to the secondary form from the current form
		 * @param {object} [fieldParams] - params to be applied to all fields; if you need different params for different fields then do it in _buildField method by overriding it
		 */
		combineFormAlongPath(addToBeginning, extendedPath, fieldParams) {
			const {combinedPathMap, defView} = this;
			const rc = defView.getRootClass();

			if (typeof extendedPath === "string") {
				extendedPath = ExtendedPath.create(rc, extendedPath, defView.pathHintsContext);
			}

			const combinedFormType = extendedPath.getEndType();
			const combinedFormView = def.getClassViewDef(extendedPath.getEndClass(), def.DEFAULT_FORM_ID);
			let fields;

			if (!fieldParams) {
				fieldParams = {};
			}

			if (combinedFormView) {
				fields = combinedFormView.m_fields;
			} else {
				// If there's no form defined for the node type, then ask for a hard-coded form
				fields = this._getCustomSecondaryFormFields(combinedFormType);
			}

			if (fields) {
				combinedPathMap.set(combinedFormType, extendedPath);

				Object.assign(fieldParams, {
					m_path: extendedPath.getStandardPath(),
					combinedFormType
				});

				// insert fields in reverse order
				_.each(addToBeginning ? fields.reverse() : fields, (field) => {
					this.insertField(lang.mixin(field, fieldParams), addToBeginning ? 0 : null);
				});
			}
		},

		/**
		 * This method is best to be called from _resolveView. It will first try to get the form for the
		 * relationship Id given from current node, then add the fields from the secondary node if any exists. If not,
		 * it will call _getCustomSecondaryFormFields method to a list of fields statically defined.
		 *
		 * @param {Boolean} addToBeginning - If true, it will add the secondary form to the beginning of the current form
		 * @param {String} relationId - relationship Id to the secondary form to be combined with the current form
		 * @param {String} [destinationClass] - targetClass will be used to get secondary form.
		 * @param {Object} [fieldParams] - holds params to be applied to all fields. If you need different params for different fields
		 * 								 then do it in _buildField method by overriding it.
		 */
		combineForm(addToBeginning, relationId, destinationClass, fieldParams) {
			const {defView} = this;
			const rc = defView.getRootClass();
			const ep = ExtendedPath.create(rc, pathCreate(relationId), defView.pathHintsContext);

			if (destinationClass) {
				ep.setEndType(destinationClass);
			}

			this.combineFormAlongPath(addToBeginning, ep, fieldParams);
		},

		postMixInProperties: function() {
			// summary: use different template for popup forms since form element will already be defined for popup
			this._defaultValues = this._defaultValues || {};
			this._fixedValues = this._fixedValues || {};
			this.combinedPathMap = new Map();
			this._formNodeURLs = [];
			this._mapSourceNodes = {};
			this._keyFieldMap = new Map();

			if (this.bPopup) {
				this.templateString = templatePopup;
			}

			this.inherited(arguments);
		},

		addDefaultValue: function(/*String*/name, /*Any*/value) {
			// summary: add default value for field whose label or id is name
			this._defaultValues[name] = value;
		},

		addFixedValue: function(/*String*/name, /*Any*/value) {
			// summary: add read-only value for field whose label or id is name
			this._fixedValues[name] = value;
		},

		addHideExpression: function(/*String*/name, /*Boolean|String*/exp) {
			// summary: control visibility for field whose label or id is name with either a boolean constant, or an expression based on current field values
			this._hideExpressions = this._hideExpressions || {};
			this._hideExpressions[name] = exp;
		},

		addRequiredExpression: function(/*String*/name, /*Boolean|String*/exp) {
			// summary: control required status for field whose label or id is name with either a boolean constant, or an expression based on current field values
			this._requiredExpressions = this._requiredExpressions || {};
			this._requiredExpressions[name] = exp;
		},

		addValueExpression: function(/*String*/name, /*Function|String*/exp) {
			// summary: control value for field whose label or id is name with either a function, or expression based on current field values
			this._valueExpressions = this._valueExpressions || {};
			this._valueExpressions[name] = exp;
		},

		focusFirstField: function() {
			// summary: put focus on first editable field

			if (!this.isValidView() || this.bSkipFocusFirstField) {
				return;
			}

			var field = _.find(this.defView.m_fields, this._isFieldActive, this);
			if (field && !(field.m_typeControl == def.FIELD_FILE)) {
				field.focusField();
			}
		},

		getDefaultValue: function(/*String*/name) {
			// summary: return fixed value for field
			return this._defaultValues[name];
		},

		getFixedValue: function(/*String*/name) {
			// summary: return fixed value for field
			return this._fixedValues[name];
		},

		getFormContext: function() {
			// summary: return context used to build and evaluate form fields
			var ctx = this.getContext();
			ctx.setNode(this.getFormNode());
			return ctx;
		},

		getFormData: function() {
			// summary: return the form data
			return this.defView.m_formData;
		},

		getFormNodeUrl: function() {
			// summary: return url for form node
			this._formNodeUrl = this._formNodeUrl || xml.getClientNodeUrl("Form");
			return this._formNodeUrl;
		},

		getFormNode: function() {
			// summary: return target node for form - will be null if creating new node unless bCurrent is true;
			// in that case, construct node either from existing node or from scratch with current field values -
			// use fake node URL so that if form is cancelled then cache will not contain wrong information
			const {cacheView, combinedPathMap, defView: {m_fields, pathHintsContext}} = this;
			const formNodeUrl = this.getFormNodeUrl();

			let node = cacheView.getNode(formNodeUrl);
			if (!node) {
				var nodeSource = this.getViewNode();
				if (nodeSource) {
					// copy node from source and update URL
					node = this._getFormNode(nodeSource.$Type, formNodeUrl, nodeSource);
				} else {
					// construct node from form values with available information
					// FIX: should this be constructed from reflection?
					var type = this.getFormNodeType();
					if (type) {
						node = this._getFormNode(type, formNodeUrl);
					} else {
						return null;
					}
				}

				// assign parent reference to that of current context unless editing;
				// in New forms, parent should be empty and so it needs to be filled
				// in Copy and New From cases, the parent may exist, but is likely to be different from a different context than current view
				if (!this.isEdit()) {
					node.__Parent__ = this._getParentURLOfClientNode();
				}
			}

			const bNew = this.isNew();
			const rc = reflection.getNodeClass(node);

			m_fields.forEach((f) => {
				// if field id is not specified, then field is not meant to apply directly to attribute;
				// handle case where field id is empty but field still resolved to attribute (used in special cases);
				// the copy form manipulates the field id, so get id from reflection attribute if present
				// FIX - don't like this - can we just change the copy form?
				const id = f.m_rca?.getId() || f.heading?.m_id;
				if (!id || f.notApplicable) {
					return;
				}

				let n = node;

				if (f.m_path) {
					const path = combinedPathMap.get(f.combinedFormType)?.toString() || f.m_path;
					const ep = ExtendedPath.create(rc, path, pathHintsContext);

					// current assumption is that path only contains child steps
					ep.steps?.forEach(({a, c}) => {
						const url = n[a];
						const nextNode = this._getFormNode(c, null, cacheView.get(url));

						if (!xml.isValidURL(url)) {
							// fill in Master and __Parent__ for new node so that path back out will work
							// FIX: not sure if we really need to check for composite relationship or we can
							// just assume it doesn't hurt to do this
							if (reflection.getNodeClass(nextNode) == rc.getRevisionClass()) { // set the parent for the revision
								nextNode.__Parent__ = nextNode.Master = n.$URL;
							}
						}
						// fill in the relationship attribute pointing to the node
						n[a] = nextNode.$URL;
						n = nextNode;
					});
				}

				// set value of attribute
				const value = this.getAttributeValueFromField(f, id, bNew ? n : null);
				xml.nodeCreateClientAttr(n, id, f.getType(), value);
			});

			return node;
		},

		getAttributeValueFromField: function(/*Field*/field, /*String*/attrId, /*Node?*/newNode) {
			// summary: return the value from the field suitable to be assigned to an attribute.
			// The default implementation returns the value from the field control if the control is present,
			// or delegate the value calculation to 'getValueForField'
			return field.m_control ? field.getValue() : this.getValueForField(field, newNode);
		},

		getTooltipRootNode: function() {
			// return the rootNode of form tooltip widget
			return this.bodyNode;
		},

		_getParentURLOfClientNode: function() {
			// summary: calculate the URL of the parent node for the node created on the client
			var parent = this._getParentOfClientNode();
			return parent == null ? csi.EMPTY_URL : parent.$URL;
		},

		_getParentTypeOfClientNode: function() {
			// summary: calculate the Type of the parent node for the node created on the client
			var parent = this._getParentOfClientNode();
			return parent == null ? "" : parent.$Type;
		},

		/**
		 * Return parent node of node being edited in form if known
		 * @return {C8Node | null}
		 */
		_getParentOfClientNode() {
			const parent = this.defView.m_formData?.getParent();
			if (parent) {
				// be aware that the FormData parent is not necessarily the parent (__Parent__) of the node in the form;
				// for example, in a view of Style Samples, the Style would be the FormData parent, while the real __Parent__
				// is a ProductSource. In such a case, we would have to return null as we don't have access to that node.
				const nodeType = this.getFormNodeType();
				const rcNode = reflection.getClass(nodeType);
				const rcNodeParents = rcNode?.getPathClasses(reflection.PATH_PARENT);
				const rcFormParent = reflection.getNodeClass(parent);

				if (rcNodeParents?.some((rcp) => rcFormParent.isDerivedFrom(rcp))) {
					this.cacheView.cacheNode(parent);
					return parent;
				}
			}

			return null;
		},

		getFormNodeType: function() {
			// summary: get target node type for form
			var node = this.getViewNode();
			if (node) {
				return xml.nodeGetType(node);
			}

			return this.defView.m_rcRoot ? this.defView.m_rcRoot.getType() : this.defView.getTypeNode();
		},

		/**
		 * Return actual node to which fields on form apply (such as in Edit case); when creating a new node
		 * either through New or Copy actions, then it is null. If there is no form data, then assume this is
		 * and edit case where form is rendered as part of another view (such as composite Size Chart edit form)
		 * @return {C8Node | null}
		 */
		getFormSourceNode() {
			const {m_formData} = this.defView;
			return m_formData ? m_formData.getNode() : this.getViewNode();
		},

		getField: function(/*String|Field*/field) {
			// summary: return field for given field name (or field itself)
			return field ? ((typeof field === "string") ? this.defView.getField(field) : field) : null;
		},

		/**
		 * Return field for given field name (or field itself)
		 * @param {String | Field} field
		 * @returns {Field}
		 */
		getFieldByUniqueName: function(field) {
			return field ? ((typeof field === "string") ? this.defView.getFieldByUniqueName(field) : field) : null;
		},

		getFieldControl: function(/*String|Field*/field) {
			// summary: return widget for field
			field = this.getField(field);
			return field ? field.m_control : null;
		},

		getFieldExpression: function(/*String|Field*/field) {
			// summary: return expression for field
			field = this.getField(field);
			var rca = field.m_rca;
			if (rca && (!this.isEdit() || !rca.isOneTimeExp)) {
				return rca.getExpression();
			}

			return null;
		},

		getFieldNode: function(/*Field*/field, /*Boolean*/bTargetOnly, /*Boolean*/bCurrent) {
			// summary: get node specified by field
			var node = bCurrent ? this.getFormNode() : this.getFormSourceNode();

			if (!node && !bTargetOnly) {
				// if copying, then use view node as source to fill in fields when copying;
				// if creating new, then use current form node (only needed for fields that
				// have paths to existing nodes)
				node = this.getViewNode() || this.cacheView.getNode(this.getFormNodeUrl());
			}

			return this.cacheView.getPathNode(node, field.m_path, true);
		},

		/**
		 * Return element marked with field, which could be a table row or cell depending on if field is combined with previous field or not (bCombineWithPrevious)
		 * @param {string | Field} field
		 * @return {HTMLElement | null}
		 */
		getFieldElement(field) {
			field = this.getField(field);
			return field ? html.queryDom([html.getDataAttributeSelector(html.DATA_FORM_UNIQUEID, field.getUniqueControlName())], this.domNode, true) : null;
		},

		/**
		 * Find row in form containing field control.
		 * @param {string | Field} field
		 * @returns {HTMLElement | null}
		 */
		getFieldRow(field) {
			field = this.getField(field);
			if (!field) {
				return null;
			}

			const dom = field.getDomNode();
			if (dom) {
				return this.getFieldRowByDomNode(dom);
			}

			const e = this.getFieldElement(field);
			return field.bCombineWithPrevious ? e?.parentNode : e;
		},

		getFieldStore: function(/*String|Field*/field, /*String*/value) {
			// summary: return store to be used in FilteringSelect control
			var ctx;
			var store;

			field = this.getField(field);

			// view must override function to get store for checkbox maps
			if (!field || (field.m_typeControl == def.FIELD_CHECKBOX_MAP)) {
				return null;
			}

			var urlRoot = this._getFieldSelectRootURL(field);
			if (urlRoot) {
				const {heading, m_path} = field;
				const isEnum = (field.getType() == xml.ATTR_ENUM);
				const r = this._getFieldRelationship(field, urlRoot, value);

				if (r) {
					// if field has own relationship, then use it
					ctx = this.getContext();
					ctx.setNodeURL(urlRoot);
					store = ctx.view.createHeadingNamesStore(ctx, field, value, r);
				} else if (heading) {
					// otherwise, look up heading for field and use its renderer to get store
					ctx = this.getFormContext();

					csiHeading.applyHeadingRenderer(ctx, heading, (renBase, hBase) => {
						if (renBase && renBase.getStore) {
							store = renBase.getStore(ctx, hBase, value, true);

							// if an aggregate store, need to add parameter for report node from form;
							// NOTE: it is still the responsibility of the controller class to reset stores explicitly
							// and manage dependencies between fields. In Forms Modernization project we will try
							// to manage them more automatically.
							const params = store?.aggregateStoreParams;
							if (params) {
								const sourceNode = this.getFormSourceNode();
								const reportNodeUrl = sourceNode?.$URL || this.getFormNodeUrl();

								// in old form/store architecture, we always create a client node that logic is based on so as to not change/overwrite existing node;
								// in new architecture, we can use existing node in Edit case since we are only applying report operations
								params.sourceUrl = reportNodeUrl;
								params.reportOperations = this._getFormReportNodeOperations(field, reportNodeUrl);
								params.actionType = this._getActionType();
								params.actionNodeType = this.getFormNodeType();

								// ctx.getNodeURL() will likely return a client node URL which needs to be substituted in
								// order to resolve to the URL of the report node along that path
								if (m_path) {
									params.sourceUrl = xml.getUrlPath(reportNodeUrl, m_path, true);
								}

								// add callback when response is ready to store with field
								store.onAggregateStoreResponse = (res) => {
									field.aggregateStoreResponse = res;

									// if slim card used, then update widget to render it rather than simple option
									if (aggregateStoreResponseHasMultipleKeys(res)) {
										field.m_control.set("renderSlimCards", true);
									}
								};
							} else {
								// reset response just in case store changes from using an aggregate store to not using one
								field.aggregateStoreResponse = null;
							}
						}
					});
				}

				if (isEnum && !field.m_id) {
					// the last chance: if it's an enumeration, use the enumeration store
					value = value || xml.enumCreate(field.m_format); // default value
					var enumId = xml.enumGetId(value);
					if (enumId) {
						store = enums.getStore(enumId, value);
					}
				}
			}

			// create empty store if none yet
			return store || this.createHeadingNamesStore(ctx || this.getContext(), field, value);
		},

		getFieldValueNode: function(/*Field*/field) {
			// summary: return DOMNode containing label for field
			var tr = this.getFieldRow(field);
			return tr ? html.queryDom([".", CSS_VALUE], tr, true) : null;
		},

		getSourceView: function() {
			// summary: return view from which form was opened
			return this.getSourceController().widget;
		},

		getValueForField: function(/*Field*/field, /*Node?*/node) {
			// summary: get value for field
			if (!field) return null;

			// determine if there is fixed or default value for this form
			var value = this.getFixedValue(field.getControlName());
			if (value != null) {
				return value;
			}

			// get the initial value from the form data
			var fd = this.defView.m_formData;
			if (fd) {
				value = fd.getFieldInitialValue(field.getControlName());
				if (value != null) {
					return value;
				}
			}

			// if it's a copy and is not copyable then get should get value from initial value on m_rca
			if (this.isCopy() && field.m_rca && !field.m_rca.isCopyable) {
				node = null;
			} else if (!node) {
				node = this.getFieldNode(field);
			}

			// get value from source node
			if (node) {
				value = node[field.m_id];

				if (value != null) {
					// make sure to disable the expression evaluation if the expression is already disabled on the source node

					// Just because an expression has been disabled should not mean it can't be restored;
					// this means that in an Edit dialog, you cannot restore an expression that has been disabled. But be careful because
					// bDisableExpressionEvaluation is forced to true in some cases, so may need to separate out cases where it should be forced to true,
					// and other cases where it is disabled but could be restored

					if (field.hasExpression() && (field.bDisableExpressionEvaluation === undefined) && !xml.isClientUrl(node.$URL)) {
						const rca = field.m_rca;
						const oneTimeExpr = rca.isOneTimeExp;
						const disabledPersistentExpr = !oneTimeExpr && xml.nodeAttributeHasFlag(node, field.m_id, xml.ATTR_FLAG_DISABLE_EXPRESSION);
						const disabledOneTimeExpr = oneTimeExpr && rca.isCopyable;

						field.bDisableExpressionEvaluation = disabledPersistentExpr || disabledOneTimeExpr;

						// this property is used to help determine if the attribute will need its expression restored
						field.startedWithDisabledContinuousExpression = disabledPersistentExpr && (rca.isCopyable || this.isEdit());
					}

					const key = fd?.getKeyContainer();
					return (key == null) ? value : value[key];
				}
			}

			// get the field value from the persistence storage
			if (field.bPersist) {
				value = fd ? fd.getPersistedFieldValue(this.getViewPreferenceContext(), field.getKey()) : null;
				if (value != null) {
					return value;
				}
			}

			// get default value - this should come after looking up the source node value
			// since the copied value should take precedence over the default value
			value = this.getDefaultValue(field.getControlName());
			if (value != null) {
				return value;
			}

			// get initial value from reflection attribute if resolved
			value = field.m_rca ? field.m_rca.getValue() : null;
			return (value == null) ? "" : value;
		},

		insertField: function(/*Field*/field, /*Number|String?*/loc, /*Boolean?*/bResolve) {
			// summary: insert field before field with given id, or at end of list
			var fields = this.defView.m_fields;

			if (bResolve || this._bResolved) {
				field.resolve(this.defView.getLocaleMap(), this.defView.m_rcRoot);
			}

			if (loc != null) {
				if (typeof loc === "string") {
					loc = csi.locate(fields, function(f) {return f.getControlName() == loc;});
				}

				if (loc >= 0) {
					fields.splice(loc, 0, field);
					return field;
				}
			}

			fields.push(field);
			return field;
		},

		/**
		 * Insert and retunr the field if not already on the form
		 * @param {string} fieldId - id of the field
		 * @param {number|string} loc - location to be inserted
		 * @param {object} [params] - optional parameter
		 */
		insertFieldIfNotExist(fieldId, loc, params) {
			let field = this.defView.getField(fieldId);
			if (!field) {
				field = this.insertField(lang.mixin(new Field(), {
					m_id: fieldId,
					...params
				}), loc);
			}
			return field;
		},

		isCreateForm: function() {
			// summary: if source action controller has no node, then form is for new node
			var fd = this.defView.m_formData;
			return fd && (fd.getSource().getNode() == null);
		},

		isFieldEditable: function(/*Field*/field) {
			// summary: determine if field is editable
			var value = this.getFixedValue(field.getControlName());
			if (value != null) {
				return false;
			}

			if (field.bForceEditable) {
				return true;
			}

			var node = this.getFieldNode(field, true);
			var rca = field.m_rca;
			var right = field.m_right;

			if (!right && node && field.m_id) {
				right = xml.RIGHT_WRITE;
			}

			if (!xml.nodeHasAccess(node, right, rca?.isReverseRelation ? null : field.m_id)) {
				return false;
			}

			if (rca) {
				const {isCreateOnly, isEditingDisabled, isTableViewOnly, isReadOnly, isReverseRelation} = rca;
				const isReverseEditable = isReverseRelation && reflection.hasAggregateStoreForAttribute(node?.$Type || field.getOwnerType(), field.m_id);
				const isEdit = this.isEdit();
				const isDefaultForm = this.defView.m_id === def.DEFAULT_FORM_ID;

				if (isDefaultForm && (isEditingDisabled || isTableViewOnly)) {
					// no form editing allowed at all on default forms based on attribute editing flags;
					// this does not apply to custom forms for now as they are OOTB and rules can be overridden there
					// (such as with Engineering Change->New Affected Specs wizard). The ProductColors field on the Style Copy form
					// would be a problem too if it weren't for custom code to set its disabled status
					return false;
				} else if (isEdit && isCreateOnly) {
					// restrict editing if attribute is create only on edit form (ok for New and Copy)
					return false;
				} else if (!isReverseEditable && !isEdit && isReadOnly) {
					// restrict editing in 'New' form if attribute is read only
					return false;
				}
			}

			return true;
		},

		isFieldVisible: function(/*Field*/field) {
			// summary: return display status of field
			var tr = field ? this.getFieldRow(field) : null;
			return tr ? !domClass.contains(tr, html.CSS_HIDE) : false;
		},

		getFieldValue: function(/*String|Field*/field, /*Boolean*/bUseDefaultIfMissing) {
			// summary: get the value of the field, using the default value if so requested and the value is not initialized yet
			field = this.getField(field);

			if (!field) {
				return null;
			}

			var value = field.getValue();
			if (value === undefined && bUseDefaultIfMissing) {
				value = this.getValueForField(field);
			}

			return value;
		},

		getFieldRowByDomNode: function(/*DOMNode*/domNode) {
			// summary: return DOMNode row associated with field id for given domNode
			return html.getNodeWithDataAttribute(domNode, html.DATA_FORM_UNIQUEID, this.domNode);
		},

		getFieldByDomNode: function(/*DOMNode*/domNode) {
			// summary: return field for given domNode
			var refFieldRow = this.getFieldRowByDomNode(domNode);
			const uniqueId = html.getDataAttribute(refFieldRow, html.DATA_FORM_UNIQUEID);
			return this.defView.m_fields.find((field) => field.getUniqueControlName() === uniqueId);
		},

		/**
		 * Extension point to allow initializing client node of specified type
		 * @param {Node} node
		 * @param {string} type
		 */
		onCreateFormNode: function(node, type) { },

		/**
		 * Extension point for writing operations to establish a report node for the form used by aggregate stores to traverse paths
		 * @param {Field} field
		 * @param {Writer} writer
		 * @param {IForm} formInfo
		 */
		onWriteFormReport(field, writer, formInfo) { },

		setFieldDisabled: function(/*String|Field*/field, /*Boolean?*/bDisabled) {
			// summary: update disabled status for field
			if (field = this.getField(field)) {
				if (field.m_control) {
					html.setDisabled(field.m_control, bDisabled);
				} else {
					field.m_right = bDisabled ? xml.RIGHT_NONE : null;
				}
			}
		},

		/**
		 * Control visibility of field, passing true to hide the field
		 * @param {string|Field} field
		 * @param {boolean} bHide
		 * @return {boolean} returns true if the visibility of the field changed
		 */
		setFieldHidden: function(field, bHide) {
			// summary: show/hide field from view; returns whether visibility changed
			field = this.getField(field);
			if (!field) return;
			var tr = this.getFieldRow(field);

			// hide field elements only if this is a combined field
			if (tr && field.bCombineWithPrevious) {
				// For every field there is field label and separator elements present.
				// we need to hide these 2 elements as well.
				var fieldDom = field.getDomNode();

				if (fieldDom) {
					var td = html.getParentByTagName(fieldDom, "td");
					var separatorDom = td && td.previousElementSibling;
					var labelDom = separatorDom && separatorDom.previousElementSibling;

					if (separatorDom) domClass.toggle(separatorDom, html.CSS_HIDE, bHide || !!field.bHideLabelForCombinedField);
					if (labelDom) domClass.toggle(labelDom, html.CSS_HIDE, bHide || !!field.bHideLabelForCombinedField);
					if (td) domClass.toggle(td, html.CSS_HIDE, bHide);
				}

				return true;
			}

			if (tr && (bHide != domClass.contains(tr, html.CSS_HIDE))) {
				domClass.toggle(tr, html.CSS_HIDE, bHide);
				return true;
			}

			return false;
		},

		setFieldRequired: function(/*String|Field*/field, /*Boolean?*/bRequired) {
			// summary: update required status for field
			field = this.getField(field);
			if (!field) {
				return;
			}

			const elem = this.getFieldElement(field);
			if (elem) {
				const separator = field.bCombineWithPrevious ? elem.nextSibling : elem.childNodes[1];
				if (separator) {
					separator.innerHTML = bRequired ? "*" : "";
				}

				const control = field.m_control;
				if (control) {
					if (html.isWidget(control)) {
						control.set("required", bRequired);
					} else {
						control.required = bRequired;
					}
				}
			}
		},

		/**
		 * Update value for field
		 * @param {string|Field} field
		 * @param {C8FieldValue} value
		 * @param {boolean} [quiet] - if true, then do not trigger an onChange event
		 */
		setFieldValue(field, value, quiet) {
			// summary: update value for field
			field = this.getField(field);

			const c = field?.m_control;
			if (!c) return;

			field._bInternalChange = true;

			if (html.isWidget(c)) {
				if (field.m_typeControl == "MultiSelect") {
					// FIX: quiet not supported for multi-select
					c.resetSelected(value);
				} else {
					c.set("value", value, quiet ? false : undefined);

					// some widget may truncate the value due to formatting (DateTime), so make sure that _valueDefault equals the widget's value
					value = field.getValue();
				}
			} else {
				c.value = (value != null ? value : "");
			}

			field._bUpdated = true;
			field._valueDefault = value;
		},

		errorForm: function(/*String*/errMessage, /*String*/errCode) {
			// summary: focus on field with error and display error message
			this.inherited(arguments);

			if (errCode) {
				this._setErrorField(_.findWhere(this.defView.m_fields, {_validationKey: errCode}));
			}

			return true;
		},

		/**
		* Rebuild form and keep certain types of the existing field values in form when save and new action occurred if using default behavior
		*/
		resetForm: function() {
			if (this.bDefaultSaveAndNew) {
				this._persistFormValues = {};

				array.map(this.defView.m_fields, function(field) {
					var bPersist = api.getFormFieldPersistException(this.getFormNodeType(), this.defView.m_id, field.m_id);
					if (bPersist || (bPersist === null && _.contains(SAVE_NEW_TYPES, field.m_type))) {
						var key = field.getUniqueControlName();
						this._persistFormValues[key] = this.getFieldValue(field);
					}
				}, this);
			}
			this.redraw();
		},

		resetFormToDefaults: function() {
			// summary: reset fields to form defaults
			array.forEach(this.defView.m_fields, function(field) {
				var value = this.getDefaultValue(field.getControlName());
				if ((value == null) && field.m_rca) {
					value = field.m_rca.getValue();
				}
				this.setFieldValue(field, (value != null) ? value : "");
			}, this);
		},

		validateForm: function(/*Object*/errorParams) {
			// summary: validate fields in form
			const {defView} = this;
			let fields = defView.m_fields;
			let field;
			let tr;

			var fieldError = this._validateFields();
			if (fieldError) {
				if (!this.isIsolatedView()) {
					errorParams.message = locale.resource("ValidateFormError");
				} else if (fieldError.m_typeControl === def.FIELD_FILE) {
					errorParams.message = locale.resource("ValidateFile");
				} else {
					errorParams.message = "";
				}

				this._setErrorField(fieldError);
				return false;
			}

			this.invalidNode.set("value", "");

			if (this._valueExpressions) {
				// set fields with default expressions as updated so they will be published
				csi.applyForEach(this._valueExpressions, function(expr, name) {
					if (field = defView.getField(name)) {
						field._bUpdated = true;
					}
				});
			}

			if (defView.m_formData) {
				// only write visible fields if so configured
				if (this.bWriteVisible) {
					fields = array.filter(fields, function(f) {
						if (tr = this.getFieldRow(f)) {
							return !domClass.contains(tr, html.CSS_HIDE);
						}

						return false;
					}, this);
				}

				defView.m_formData.setFields(fields);
				defView.m_formData.savePersistedFieldValues(this.getViewPreferenceContext(), fields);
			}

			return true;
		},

		/**
		 * Add default and fixed values to form based on user defaults
		 * @param {string} type - type of node being created
		 * @param {Field[]} fields
		 */
		_applyUserDefaults: function(type, fields) {
			if (!configPreferences.getUserDefaultsForType(type)) {
				return;
			}

			_.each(fields, function(f) {
				var ud = f.m_id && configPreferences.getUserDefaultForAttribute(type, f.m_id, f.m_path);
				if (ud) {
					var name = f.getControlName();
					var value = ud.getValueForTargetAttributeType();
					if (value != null) {
						if (ud.enforced) {
							if (this.getFixedValue(name) == null) {
								this.addFixedValue(name, value);
							}
						} else if (this.getDefaultValue(name) == null) {
							this.addDefaultValue(name, value);
						}
					}
				}
			}, this);
		},

		/**
		 * Return true if able to write form report for field
		 * @param {Field} field
		 * @return {boolean}
		 */
		_canWriteFormReportForField(field) {
			return !!field.m_rca &&
				!field.omitFromReport &&
				(field.m_typeControl !== def.FIELD_FILE);
		},

		_getFormNode: function(/*String*/type, /*String?*/url, /*Node?*/nodeSource) {
			// summary: create temporary node for view to store form values
			var node;
			var urlSource;

			if (nodeSource) {
				urlSource = nodeSource.$URL;

				// if source node is form node, then return it
				if (xml.isClientUrl(urlSource)) {
					return nodeSource;
				}

				// if form node already created for given source, then return it
				var urlFormNode = this._mapSourceNodes[urlSource];
				if (urlFormNode) {
					node = this.cacheView.getNode(urlFormNode);
					console.assert(!!node, "Node must exist!");
					return node;
				}
			}

			// if no url is given, then make one up
			url = url || xml.getClientNodeUrl(type);

			// store list of all form nodes - they will be removed from cache when view is reset (refreshed)
			this._formNodeURLs.push(url);

			if (nodeSource) {
				// if source node given, then make a copy and assign new url
				node = xml.nodeCopy(nodeSource);
				node.$URL = url;
				this.cacheView.cacheNode(node);

				// ensure that attributes with No Copy constraint have value reset
				// TODO: this allows for attributes not of the form to contribute to expressions, but does not
				// take copy options into account; ideally, on the copy form if a copy option is set or unset,
				// these values would get (re)set appropriately
				if (this.isCopy()) {
					var rc = reflection.getNodeClass(node);

					_.each(node, function(value, id) {
						var rca = rc && rc.getAttribute(id, true, false, true);
						if (rca && !rca.isCopyable) {
							node[id] = rca.getValue();
						}
					});
				}

				// change attributes of node being copied to reference new nodes already copied
				_.each(this._mapSourceNodes, function(urlNewNode, urlSourceNode) {
					_.each(node, function(value, id) {
						if (value == urlSourceNode) {
							node[id] = urlNewNode;
						}
					});
				});

				// track mapping of copied nodes
				this._mapSourceNodes[urlSource] = url;

				// when node is copied, then all form nodes pointing to the original must now point to copy
				_.each(this._formNodeURLs, function(urlFormNode) {
					var formNode = this.cacheView.getNode(urlFormNode);

					_.each(formNode, function(value, id) {
						if (value == urlSource) {
							formNode[id] = url;
						}
					});
				}, this);

				// when editing a node, track source node on it for use in evaluating query filter expressions
				if (this.isEdit()) {
					node[xml.ATTR_CLIENT_SOURCE_URL] = urlSource;
				}
			} else {
				// if no source node given, then just create empty node of given type
				node = this.cacheView.createClientNode(url, type);

				// set name to undefined so that the normally empty string value for the name will not override the default value for the name field
				node[xml.ATTR_NAME] = null;

				// call extension point to populate attribute values (most likely relations to other nodes in the cache)
				this.onCreateFormNode(node, type);
			}

			return node;
		},

		/**
		 * Return value to be published for field
		 * @param {C8Node} formNode
		 * @param {Field} field
		 * @return {C8AttributeValue}
		 */
		_getFormReportFieldValue(formNode, field) {
			// if performing Save & New, then use value that will be applied whether or not it has been applied yet
			const key = field.getUniqueControlName();
			let value = this._persistFormValues?.[key];

			if (isNullOrUndefined(value)) {
				const fieldNode = this.cacheView.getPathNode(formNode, field.m_path);
				value = fieldNode?.[field.m_id];
			}

			return value;
		},

		/**
		 * Return report nodes operations script to publish changes in form
		 * @param {cnl} nodeUrl - report node url which will only be an existing url in the Edit case
		 * @return {string}
		 */
		_getFormReportNodeOperations(field, nodeUrl) {
			const {cacheView, combinedPathMap, defView, formInfo, _mapSourceNodes} = this;
			const formNode = this.getFormNode();
			const sourceNode = this.getFormSourceNode();
			const type = this.getFormNodeType();
			const writer = new Writer();
			writer.setReport();

			// NOTE: this will be done a bit differently in React, but to keep consistent with current implementation,
			// use existing form functionality for some properties rather than rely on controller class
			const thisFormInfo = {
				...formInfo,
				nodeType: type,
				nodeUrl,
				sourceNodeUrl: sourceNode?.$URL,
			};

			// set parent information if not already specified - even if it overrides what is specified from the action controller
			// as the base controllers do not take all special cases into account; this again will change with the new form architecture
			const parentUrl = this._getParentURLOfClientNode();

			if (xml.isValidURL(parentUrl) && !this.isEdit()) {
				thisFormInfo.parentUrl = parentUrl;
				thisFormInfo.parentAttributeId = reflection.getCompositeAttributeIdFromTo(this._getParentTypeOfClientNode(), type);
			}

			// if New or Copy case, then create new node
			if (!sourceNode) {
				writer.createNode(nodeUrl, type);
			}

			// create nodes along combined paths if necessary
			if (this.isCombinedForm()) {
				writeFormPaths(writer, cacheView, formNode, [...combinedPathMap.values()], _.invert(_mapSourceNodes));
			}

			// write custom operations to establish proper context
			this.onWriteFormReport(field, writer, thisFormInfo);

			// attach to context/parent if known
			writeFormParent(writer, thisFormInfo);

			// write node operations for form fields that are attributes
			writeFormFields(
				writer,
				nodeUrl,
				defView.m_fields,
				false,
				(f) => this._canWriteFormReportForField(f),
				(f) => this._getFormReportFieldValue(formNode, f),
			);

			return writer.serialize();
		},

		/**
		 * @override
		 * Resize all children when resizing form
		 */
		_onResize: function() {
			_.each(this.getChildren(), function(child) {
				if (child.resize) {
					child.resize();
				}
			});
		},

		_onRootDependencyPaths: function(/*String[]*/paths) {
			// summary: add any paths for fields
			var p;

			array.forEach(this.defView.m_fields, function(f) {
				if (p = f.getQueryPath()) {
					paths.push(p);
				}
			});
		},

		_validateField: function(/*Field*/field, /*Boolean*/bSubset) {
			try {
				var value = field.getValue();

				if (field.ren) {
					field.ren.validateValue(this.getContext(), field.heading, value);
				}

				// validate type, format and validation expression
				field._validationKey = csiHeading.validateValue(field.getValue(), field.getType(), field.m_format, field.isControlRequired(), field.m_rca, bSubset);

				// test custom validation when blurred
				this._fieldEvent(field, field.m_onValidate, {bSubset: bSubset});
			} catch (ex) {
				return ex.message;
			}

			return null;
		},

		_validateFields: function() {
			// summary: validate all form fields, returning the first with an error
			var fieldError = null;

			array.forEach(this.defView.m_fields, function(field) {
				var control = field.m_control;

				if (!control || control.disabled || !html.isVisible(control) || !html.isVisible(this.getFieldRow(field))) {
					return;
				}

				var validationWidget = control.formValidationNode || control;

				if (validationWidget.validate || validationWidget.isValid) {
					// see dijit/form/_FormMixin
					validationWidget._hasBeenBlurred = true;

					if (!html.validateWidget(validationWidget)) {
						fieldError = fieldError || field;
					}
				} else {
					// validate fields using widgets that do not support own validation
					if (this._validateField(field)) {
						fieldError = fieldError || field;
					}
				}
			}, this);

			return fieldError;
		},

		/**
		 * Augment store with Go to Dialog action as appropriate
		 * @param {Field} field
		 * @param {any} store
		 * @return {boolean} - returns true if store has already been set on field (when delayed search is enabled)
		 */
		_enableGoToDialogForField(field, store) {
			if (!store || store.enumId) {
				return false;
			}

			let {action, aggregateStoreParams} = store;
			if (!action) {
				if (aggregateStoreParams) {
					action = store.action = new Action();
				} else {
					return false;
				}
			} else if (action.classController === GoToDialogController) {
				// already augmented (just in case)
				return false;
			}

			const nodeEdit = this.getFieldNode(field, false, true);
			const {heading, m_control, m_label} = field;
			const ha = heading?.m_action;
			const rca = heading?._rca;

			// finish configuring action
			action.m_id = action.m_id || ha?.m_id || ("FormAggregateInline" + field.getControlName());
			action.m_name = ha?.m_name;
			action.m_tooltip = action.m_tooltip || ha?.m_tooltip || locale.resource("AggregateInline_Name", [m_label]);

			action.classController = GoToDialogController;
			action.classControllerParams = {
				...action.classControllerParams,
				dialogCardId: field.dialogCardId,
				goToDialogStore: store,
				rca,
				m_urlNode: nodeEdit?.$URL,
				widget: this,

				onFormData: (fd) => {
					if (field.m_bRequired) {
						fd.m_bRequireOne = true;
					}
				}
			};

			store.actionData = [csiStore.nameObject(locale.resource("GoToDialog"), csi.DIALOG_URL)];

			if (rca?.isDelayedSearch) {
				// setup widget to use delayed search
				m_control.enableDelayedSearch(store, () => m_control.goToDialog());

				// cannot cache store in this case since it will only have partial results
				this.refreshHeadingStore(heading);

				return true;
			}

			return false;
		},

		/**
		 * Finish initialization of new store and apply to field
		 * @param {Field} field
		 * @param {any} store
		 * @param {boolean} [restartQueryInProgress]
		 */
		_setFieldStore(field, store, restartQueryInProgress) {
			const {m_control} = field;

			if (m_control && !this._enableGoToDialogForField(field, store) && (store !== m_control.store)) {
				const queryInProgress = !!m_control.isQueryInProgress?.();

				m_control.set("store", store);

				if (queryInProgress && restartQueryInProgress) {
					m_control.toggleDropDown();
				}
			}
		},

		_buildField: function(/*Context*/ctx, /*Field*/field, /*DOMNode*/tr, /*DOMNode?*/tdControl) {
			var node = this.getFieldNode(field);
			var controlType = field.m_typeControl;
			var control = null;
			var self = this;
			var div;
			var th;
			var tdSeparator;
			var td;
			var fOnDraw;

			if (!field.bCombineWithPrevious && !field._metaDataField) {
				html.setDataAttribute(tr, html.DATA_FORM_FIELD, field.getControlName());
				html.setDataAttribute(tr, html.DATA_FORM_UNIQUEID, field.getUniqueControlName());
			}

			if (!controlType && !field.m_onDraw) {
				td = html.create("td", null, tr);
				def.processClass(field, tr, "csi-form-view-spacer", node, this.cacheView);
			} else if (controlType == def.FIELD_COMMENT) {
				td = html.create("td", {
					colSpan: 3,
					multiLineText: field.m_label
				}, tr);

				def.processClass(field, tr, "csi-form-view-comment", node, this.cacheView);
			} else {
				// field is editable if view has no root node, or if node is editable
				var value = this.getValueForField(field, node);
				var bEdit = this.isFieldEditable(field);
				var bRequired = bEdit && field.m_bRequired;
				var bInitialize = bEdit && field.m_bInitialize;
				var bUseRenderer = !!field.ren && this._useRendererForField(field);
				var widgetParams = _getWidgetParams(ctx, field, this._keyFieldMap);
				var store = widgetParams.store;

				// process field rules to determine if field should start out disabled
				if (bEdit) {
					field.bCanEdit = true;
					bEdit = this._doesFieldPassRules(field, node);
				}

				widgetParams.customErrorHandling = true;
				widgetParams.disabled = !bEdit;
				widgetParams.required = bRequired;
				widgetParams.onFocus = lang.hitch(this, this._onFocusField, field, ctx);

				if (tdControl) {
					td = tdControl;
				} else {
					def.processClass(field, tr, tr.className, node, this.cacheView);

					// output field name
					th = html.create("th", {
						innerText: field.m_label
					}, tr);

					// output separator - show "*" even if not editable, but don't pass that on to the widget
					tdSeparator = this._addSeparator(tr, field.m_bRequired);

					// output field control
					td = html.create("td", {
						"class": CSS_VALUE
					}, tr);
				}

				//output the input widget control
				div = html.create("div", {
					"class": CSS_VALUE_INPUT
				}, td);

				if (_containsCombinedField(tr) || _containsMetaDataField(td)) {
					html.setDataAttribute(td, html.DATA_FORM_FIELD, field.getControlName());
					html.setDataAttribute(td, html.DATA_FORM_UNIQUEID, field.getUniqueControlName());
				}

				field._bUpdated = false;
				field._valueDefault = null;

				if (controlType == def.FIELD_TEXTAREA) {
					if (th) {
						domClass.add(th, "csiFormViewAlignTop");
						domClass.add(tdSeparator, "csiFormViewAlignTop");
					}
				} else if (controlType == def.FIELD_PASSWORD) {
					widgetParams.type = "password";
					widgetParams.onChange = lang.hitch(this, "onChangeField", field);
					control = field.m_control = new ValidationTextBox(widgetParams).placeAt(div);
				} else if (controlType == def.FIELD_FILTERING_SELECT) {
					if (!store) {
						store = widgetParams.store = this.getFieldStore(field, value);
					}

					// when field is not attribute it is possible that default value is not correct, so ask store for default
					if (!value && store != null && store.getEmptyValue) {
						value = store.getEmptyValue();
					}

					if (!bUseRenderer) {
						widgetParams.onChange = lang.hitch(this, "onChangeField", field);
						widgetParams.value = value;
						control = field.m_control = new RefSelect(widgetParams, domConstruct.create("div", null, div));

						// add information to store to enable Go to Dialog
						this._setFieldStore(field, store);

						control.startup();

						// if required field, then start query right away and select result if only one
						if (bRequired || bInitialize) {
							const promise = util.wait().then(() => this._getDefaultStoreValue(field, value));
							this._deferredList.push(promise);
						}
					}
				} else if (controlType == def.FIELD_MULTI_SELECT) {
					// FIX: does not support renderer because in some mass-create form cases the multi-select widget is used with a ref renderer
					if (!store) {
						store = widgetParams.store = this.getFieldStore(field, value);
					}

					if (value == csi.EMPTY_URL || _.isEqual(value, [csi.EMPTY_URL]) || !value) {
						value = [];
					}

					// remove csi.EMPTY_URL from store so it wont be count as an item
					store.remove(csi.EMPTY_URL);

					widgetParams.onChange = lang.hitch(this, "onChangeField", field);
					widgetParams.value = [].concat(value);

					control = field.m_control = new CheckedMultiRefSelect(widgetParams, domConstruct.create("div", null, div));

					// add information to store to enable Go to Dialog
					this._setFieldStore(field, store);

					control.startup();

					if (bRequired || bInitialize || widgetParams.defaultAll) {
						const promise = util.wait().then(() => this._getDefaultStoreValue(field, value));
						this._deferredList.push(promise);
					}
				} else if (controlType == def.FIELD_CHECKBOX_MAP) {
					if (!store) {
						// create an empty store to satisfy the ForestStoreModel if not otherwise specified
						store = this.getFieldStore(field) || new ItemFileReadStore({data: {items: []}});
					}

					widgetParams.model = new ForestStoreModel({
						query: {type: "group"},
						store: store
					});
					widgetParams.showRoot = false;
					widgetParams.onTreeChecked = lang.hitch(this, "onChangeField", field);
					widgetParams.onTreeExpanded = lang.hitch(this, "publishDomChange", div);

					control = field.m_control = new CheckboxTree(widgetParams, domConstruct.create("div", null, div));
					control.startup();
				} else if (controlType == def.FIELD_FILE) {
					bUseRenderer = false;
					this._handleFileField(field, widgetParams, control, td, div);
				}

				if (bUseRenderer) {
					widgetParams.value = value;
					widgetParams.onChange = lang.hitch(this, "onChangeField", field);

					control = field.m_control = field.ren.toHtmlDynamic(ctx, field.heading, div, widgetParams);
					control.placeAt(div);
					control.startup();
				}
			}

			// call custom draw function
			if (fOnDraw = csi.resolveFunction(field.m_onDraw, this)) {
				fOnDraw(this.defView, field, value, div, control, widgetParams);
				control = control || field.m_control;
			}

			// handle validation message
			const messageClass = field.messageClass;
			var messageCtr = messageClass ? csi.syncRequire(messageClass) : null;
			var validationWidget;

			if (control) {
				validationWidget = control.formValidationNode || control;

				if (!messageCtr && validationWidget.displayMessage) {
					messageCtr = ErrorText;
				}

				// Firefox handles css autocompletion setting differently
				if (validationWidget.textbox) {
					validationWidget.textbox.autocomplete = "off";
				}

				// setup validation for controls that support it
				// TODO: move this to widget class rather than do here?
				if (validationWidget.displayMessage) {
					if (validationWidget.isValid) {
						validationWidget.own(aspect.around(validationWidget, "isValid", function(originalFunction) {
							return function(isFocused) {
								var bValid = originalFunction.apply(this, arguments);
								if (bValid) {
									var message = self._validateField(field, isFocused && !this._hasBeenBlurred);
									validationWidget.set("invalidMessage", message || "");
									bValid = !message;
								} else {
									validationWidget.set("invalidMessage", widgetParams.invalidMessage || this.messages.invalidMessage);
								}

								return bValid;
							};
						}));
					}

					if (validationWidget.onInput) {
						validationWidget.own(aspect.after(validationWidget, "onInput", function() {
							validationWidget._hasBeenBlurred = false;
						}));
					}

					if (validationWidget._isValidSubset) {
						validationWidget.own(aspect.around(validationWidget, "_isValidSubset", function(originalFunction) {
							return function() {
								var bValid = originalFunction.apply(this, arguments);
								if (bValid) {
									var message = self._validateField(field, true);
									bValid = !message;
								}

								return bValid;
							};
						}));
					}
				}
			}

			if (messageCtr) {
				field.messageNode = new messageCtr({
					inputWidget: validationWidget,
					"class": "csi-error-message"
				}).placeAt(td);
			} else {
				domConstruct.create("div", {"class": "csi-form-reserve-for-field-noerror"}, td);
			}

			return control;
		},

		_handleFileField(field, widgetParams, control, td, div) {
			widgetParams.type = "file";
			control = field.m_control = domConstruct.create("input", widgetParams, div);
			domClass.add(td, "csiFormViewValueFile");
			this.own(on(control, "change", lang.hitch(this, this.onChangeField, field)));
		},

		_onFocusField: function(/*Field*/field,/*Context*/ctx) {
			if (!field.m_control) {
				return;
			}

			var params = {};
			params.field = field;
			params.ctx = ctx;
			var targetNode = field.m_control.domNode;
			this.tooltipView._onDeck = {
				connectNode: targetNode,
				refNode: targetNode
			};
			this.tooltipView.open(targetNode, params);
		},

		areSameFieldIds: function(/*String*/fieldOrAttrId1, /*String*/fieldOrAttrId2) {
			// summary: returns true if field/attribute ids are the same.
			// Default behavior is compare as strings.
			// Some forms can override this behavior if they have different mapping between fields and attributes
			return fieldOrAttrId1 == fieldOrAttrId2;
		},

		/**
		 * Extension point to return field dependant value
		 * @param {Field} field
		 * @param {Field} fieldSource
		 * @param {Node} node
		 * @return {Any} default field value from the node
		 */
		getFieldDependentValue: function(field, fieldSource, node) { },

		/**
		 * Extension point called after processing any field changes
		 * @param {Field} field
		 */
		onAfterChangeField(field) { },

		/**
		 * Function called when field changes value; notify dependencies, indicate control is updated and call onchange event if given
		 * @param {Field} field
		 */
		async onChangeField(field) {
			const {defView: {m_fields}} = this;
			const {combinedFormType, m_control, m_id, m_onChange, m_path} = field;

			if (!m_control) {
				return;
			}

			// if ref field, cache the selected node
			const pathToRef = (m_id && (field.getType() == xml.ATTR_REF)) ? xml.pathCreate(xml.PATH_OP_CHILD, m_id) : null;
			let value = field.getValue();
			let node = pathToRef ? await this.cacheView.getNodeDeferred(value) : null;

			// set control as updated and call callback
			field._bUpdated = true;
			this._fieldEvent(field, m_onChange);

			// update dependencies if any
			value = field.getValue();
			field._bUpdated = !field.hasExpression() || (value !== field._expressionResult);

			node = pathToRef ? await this.cacheView.getNodeDeferred(value) : null;
			const id = field.getControlName();
			const combinedName = this.getCombinedFieldName(combinedFormType, m_id);

			if (id) {
				m_fields.forEach((f) => {
					const dependsInfo = f.m_rca?.getDependsOnInfo();
					const dependsOnPath = dependsInfo?.path?.getStandardPath();

					if (this.areSameFieldIds(f.m_dependency, id) && (f.m_path == m_path)) {
						// handle dependency between two fields
						this._onDependency(f, field);
					} else if (node && f.m_id && (f.m_path == pathToRef)) {
						// update value of field based on selection
						this.setFieldValue(f, node[f.m_id]);
					} else if (dependsInfo && !dependsOnPath) {
						// handle linked enumeration relation between two fields, take the secondary type into account
						const dependsOnCombinedName = this.getCombinedFieldName(f.combinedFormType, dependsInfo.attr.m_id);

						if (this.areSameFieldIds(combinedName, dependsOnCombinedName)) {
							// NOTE: enums cannot be dependent on an enumlist, so value must be a string
							if (this.getField(dependsOnCombinedName).getType() == xml.ATTR_REF) {
								this._onDependsOn(f, !!xml.isValidURL(value));
							} else {
								this._onDependsOn(f, !!xml.enumGetText(value));
							}
						}
					}
				});
			}

			// call extension point
			this.onAfterChangeField(field);

			// update fields with expressions if required
			this._updateExpressionFields();
			this._updateFieldEditability();

			// reset all other fields' aggregate stores now that a field has changed its value
			if (id) {
				await this._resetAggregateStores(field);
			}

			// if fields were hidden publish the change event (popup dialog might need to be resized)
			if (this._updateFieldVisibility(field)) {
				this.publishDomChange();
			}

			// update focused field based on changes in case some fields were enabled/disabled/hidden/shown;
			// do not update focus unless change came from user, rather than a change caused programmatically
			if (field._bInternalChange) {
				field._bInternalChange = false;
			} else {
				this._updateFocusField(field);
			}

		},

		onClick: function(/*Event*/e) {
			// summary: onClick called when ENTER key hit on field. Make sure current widget's value is confirmed before saving.
			if (e.target && e.target.type == "submit") {
				event.stop(e);

				// focus on submit button to make sure that any field that was being edited loses focus and registers change (calls onChangeField)
				focus.focus(e.target);

				// delay execution of Save action for a brief time to allow onChange events to fire
				if (!this.bSubmitDelay) {
					this.bSubmitDelay = _.delay(function(self) {
						self.bSubmitDelay = null;
						Widget.prototype.onClick.call(self, e);
					}, 200, this);
				}

				return true;
			} else if (e.target) {
				// if click somewhere on the form other than the button or the field, set field to null
				var field = e.target.type ? this.getFieldByDomNode(e.target) : null;

				if (field && field.m_control) {
					if (field.m_control.focus) {
						field.m_control.focus();
					}
					this._onFocusField(field, this.getFormContext());
				} else {
					this.tooltipView.close();
				}
			}

			return this.inherited(arguments);
		},

		onSetDefaultValue: function(/*Field*/field, /*Store*/store, /*Any*/value) {
			// summary: handle results of required field - default is to automatically select value if only one valid entry;
			// value is false if there are multiple values, and null if no non-empty values
			if (!_.isEqual(value, field.getValue())) {
				this.setFieldValue(field, value);
			}
		},

		/**
		 * Recreate stores of all aggregate stores that pass the filter function; if not specified, then reset all
		 * @param {(field: Field) => void} [filterFunc]
		 */
		resetAggregateStores(filterFunc) {
			this.defView.m_fields.forEach((field) => {
				if (!filterFunc || filterFunc(field)) {
					const store = field.getStore();

					if (store?.aggregateStoreParams) {
						this.resetFieldStoreAndValue(field);
					}
				}
			});
		},

		/**
		 * Reset store-based field with new store and value
		 * @param {string|Field} field
		 * @param {any} [value]
		 */
		resetFieldStoreAndValue: function(field, value) {
			field = this.getField(field);

			// if field does not exist, or is not rendered yet, then let _buildField do the job
			const control = field?.m_control;
			if (!control) {
				return;
			}

			const store = this.getFieldStore(field, value);

			if (value == null) {
				if (field.m_typeControl == def.FIELD_MULTI_SELECT) {
					value = [];
				} else {
					value = store.getEmptyValue ? store.getEmptyValue() : "";
				}
			}

			this.setFieldStore(field, store);
			this.setFieldValue(field, value);

			if (field.isControlRequired() || field.m_bInitialize || control.defaultAll) {
				this._getDefaultStoreValue(field, value);
			}

			field._bUpdated = true;
		},

		/**
		 * Set the store for the field
		 * @param {String|Field} field
		 * @param {Store} store
		 */
		setFieldStore: function(field, store) {
			field = this.getField(field);

			if (field?.m_control) {
				this._setFieldStore(field, store);
			}
		},

		_onDependency: function(/*Field*/field, /*Field*/fieldSource, /*Any?*/value) {
			// summary: handle dependencies between fields
			if ((field.m_typeControl == def.FIELD_FILTERING_SELECT) || (field.m_typeControl == def.FIELD_MULTI_SELECT)) {
				// we should never change a fixed value, even if it depends on another heading.
				if (this.getFixedValue(field.getControlName())) {
					return;
				}

				// refresh store so we get a new store below
				if (field.heading) {
					// match code in getFieldStore so that store is refreshed with correct url (key)
					var ctx = this.getFormContext();

					csiHeading.applyHeadingRenderer(ctx, field.heading, function(renBase, hBase) {
						ctx.view.refreshHeadingStore(hBase, ctx.getNodeURL());
					});
				}

				if ((value == null) && fieldSource) {
					var node = this.cacheView.getNode(fieldSource.getValue());
					value = this.getFieldDependentValue(field, fieldSource, node);
				}

				this.resetFieldStoreAndValue(field, value);

				var fOnDraw = csi.resolveFunction(field.m_onDraw, this);
				if (fOnDraw) {
					var ctr = field.m_control;
					var parentNode = html.isWidget(ctr) ? ctr.domNode.parentNode : ctr.parentNode;
					fOnDraw(this.defView, field, field.getValue(), parentNode, field.m_control);
				}
			} else if ((fieldSource.m_typeControl == def.FIELD_FILTERING_SELECT) && (field.m_typeControl == def.FIELD_TEXT)) {
				// initialize text from selected value if not changed explicitly
				var text = fieldSource.m_control.get("displayedValue");
				if (text && field.isDefaultValue()) {
					this.setFieldValue(field, text);
				}
			}
		},

		/**
		 * Update dependent enum field
		 * @param {Field} field
		 * @param {boolean} bValid
		 */
		_onDependsOn(field, bValid) {
			// set the empty value for dependent field
			const existingValue = field.getValue();
			const existingStore = field.getStore();
			const emptyValue = existingStore.getEmptyValue();
			this.setFieldValue(field, emptyValue);

			const bSingle = (field.getType() === xml.ATTR_ENUM || field.getType() === xml.ATTR_REF);
			const store = this.getFieldStore(field);

			// if existing value(s) are invalid, then they will be cleared (rather than retained and invalid in store)
			store.omitInvalid = true;

			// for dependent - always query to get the dependent values
			this.when(store.query(), (result) => {
				const { isAutofillDependentEnum, isAutofillLookupItem, }  = field.m_rca;
				const dfdAutofill = (isAutofillDependentEnum || isAutofillLookupItem)  ? store.getDefaultValue() : null;
				
				this.when(dfdAutofill, (autoFillValue) => {
					this.setFieldDisabled(field, !!store.isStoreEmpty?.());
					this.setFieldStore(field, store);

					let newValue = autoFillValue || emptyValue;
					// for multi select
					if (!bSingle) {
						newValue = (newValue === emptyValue) ? [] : [newValue];
						const existingValidValues = existingValue.filter(existingSelectedItem => store.getFromMemory(existingSelectedItem) != null);
						if (!_.isEmpty(existingValidValues)) {
							newValue = existingValidValues;
						}
					} else {
						if ((existingValue != emptyValue) && store.getFromMemory(existingValue) != null) {
							newValue = existingValue;
						}
					}
					this.setFieldValue(field, newValue);
				});
			});
		},

		/**
		 * Destroy and rebuild a field
		 * @param {Field} field - The field we're rebuilding
		 */
		_rebuildField: function(field) {
			var ctx = this.getFormContext();
			var tr = this.getFieldRow(field);
			html.destroyChildren(tr);
			this._buildField(ctx, field, tr);
		},

		_redraw: function() {
			// summary: clean body before refreshing

			// clear out form nodes so they will be regenerated on refresh
			_.each(this._formNodeURLs, this.cacheView.removeNode, this.cacheView);
			this._formNodeURLs = [];
			this._formNodeUrl = null;
			this._mapSourceNodes = {};

			// reset form data
			this.defView.m_formData?.setFields(null);

			array.forEach(this.defView.m_fields, function(f) {
				// reset field control before building new ones
				f.m_control = null;

				// refresh stores for dependent fields - only refresh those that are
				// associated with the form url; any others can continue to be cached
				if (f.m_dependency) {
					this.refreshHeadingStore(f.heading);
				}
			}, this);

			// hide any errors
			this.invalidNode.set("value", "");

			// fade out existing form while building new form if in dialog
			if (this.scrollNode) {
				this._resetBodyNode = this.bodyNode;
				this.bodyNode = domConstruct.create("tbody");
				domStyle.set(this.scrollNode, "overflow", "hidden");
				fx.fadeOut({
					duration: 250,
					node: this._resetBodyNode,
					onEnd: lang.hitch(this, this._onFadeBody)
				}).play();
			} else {
				html.destroyChildren(this.bodyNode);
			}

			this.inherited(arguments);
		},

		_buildFields: function(/*Field[]*/fields) {
			// summary: apply user default values and render fields to form

			// apply user defaults - do this as last step before rendering fields so that all other methods of applying
			// default and fixed values would be done by now; they will override any user defaults
			// NOTE: make sure getFormContext() is called after applying any user defaults or else the default values will not
			// be  applied (getFormNode will exist and take precedence)
			if (!this.isEdit()) {
				this._applyUserDefaults(this.getFormNodeType(), fields);
			}

			// render
			var ctx = this.getFormContext();
			var tr;

			array.forEach(fields, function(f) {
				if (this._isFieldBuildable(f)) {
					tr = this._createFieldDom(tr, f);
					this._buildField(ctx, f, tr);
				}
			}, this);
		},

		/**
		 * Decides whether we should build the field or not. We don't allow the field to be built if it's hidden
		 * @param {Field} field
		 * @return {boolean}
		 */
		_isFieldBuildable: function(field) {
			return !field.bHidden;
		},

		/**
		 * Creates a Dom object for the field passed. If the prevRow is set and bCombineWithPrevious flag is set on the field
		 * passed it will be combined with the previous row. Also if refNode and position params are set, it will use them
		 * to place the new Dom.
		 * @param {DOMNode} prevRow - The row to combine the new field DOMNode with.
		 * @param {Field} [field] - The field we're creating DOMNode for.
		 * @param {DOMNode} [refNode] - we use the position param against this DOMNode (defaults to form bodyNode)
		 * @param {String} [position] - possible values: "first","after","before","last" (is the default), "replace" or "only"
		 * @return {DOMNode} returns the row for the field passed.
		 */
		_createFieldDom: function(prevRow, field, refNode, position) {
			var tr;

			if (prevRow && field && field.bCombineWithPrevious) {
				domClass.add(prevRow, "csi-form-view-row-combined");
				tr = prevRow;
			} else {
				tr = domConstruct.create("tr", {"class": "csiFormViewRow"}, refNode || this.bodyNode, position);
			}

			return tr;
		},

		_buildRightWidget: function() {
			if (this.rightWidgetClass && !this.rightWidget) {
				this.rightWidget = new this.rightWidgetClass(this.rightWidgetProperties).placeAt(this.rightWidgetNode);
				html.setVisible(this.rightWidgetNode, true);
			}
		},

		_buildForm: function() {
			this._deferredList = [];
			this._buildFields([].concat(this.defView.m_fields));
			this._buildRightWidget();
			this._updateExpressionFields();
			this._updateDependentEnums();
			this._updateFieldVisibility();
		},

		_buildView: function() {
			// summary: render all fields
			if (this._resetBodyNode) {
				// redraw case
				setTimeout(() => {
					this._buildForm();

					this.when(this._setPersistFieldsValue(), () => {
						this._onFadeBody();
						this._postView();
					});
				}, 100);
			} else {
				// focus on first element after delay - doesn't seem to work any other way
				this._buildForm();

				setTimeout(() => {
					this.focusFirstField();
				}, 200);

				this._postView();
			}
		},

		/**
		 * Set fields value in the form when save&new button is clicked and  fields default value are loaded from the store
		 * @return {Deferred | null}
		 */
		_setPersistFieldsValue() {
			const {defView, _persistFormValues} = this;
			const fields = defView.m_fields;

			if (!_persistFormValues) {
				return null;
			}

			const waitForDone = (dfd) => {
				// wait if there are change handles on any field (change in progress)
				const inProgress = fields.some((f) => !!f.m_control?._onChangeHandle);
				if (inProgress) {
					_.defer(() => {
						waitForDone(dfd);
					});
				} else {
					dfd.resolve();
				}
			};

			const processField = async (fieldName, value) => {
				// query store before setting value, as the store will be queried anyways when setting the value;
				// this way the timing is under our control
				const field = this.getFieldByUniqueName(fieldName);
				const store = field.getStore();
				await store?.ensureStoreIsLoaded?.();

				// set field value and wait for it to complete
				this.onSetDefaultValue(field, store, value);

				const dfd = new Deferred();
				waitForDone(dfd);
				return dfd;
			};

			let dfdAll = all(this._deferredList);

			// build chain of events to set each field's value one by one
			fields.map((field) => {
				const key = field.getUniqueControlName();
				const value = _persistFormValues[key];

				if (value != null) {
					dfdAll = dfdAll.then(() => processField(key, value));
				}
			});

			return dfdAll.then(() => {
				// reset property when done applying everything
				this._persistFormValues = null;
			});
		},

		_addSeparator: function(/*DOMNode*/tr, /*Boolean*/bRequired) {
			// summary: output cell for separator
			return html.create("td", {
				"class": "csiFormViewSeparator",
				innerText: bRequired ? "*" : null
			}, tr);
		},

		/**
		 * Return true if field passes edit rules and should be enabled (styling rules are skipped)
		 * @param {Field} field
		 * @param {Node} node
		 * @return {boolean}
		 */
		_doesFieldPassRules: function(field, node) {
			var cache = this.cacheView;

			return _.every(field.m_rules, function(r) {
				return !!r.m_class || r.passes(node, cache);
			});
		},

		_fieldEvent: function(/*Field*/field, /*String|Function*/callback, /*Object?*/params) {
			// summary: react to event on Field
			if (callback = csi.resolveFunction(callback, this)) {
				return callback(this.defView, field, field.getValue(), params || {});
			}

			return true;
		},

		_getDefaultStoreValue: function(/*Field*/f, /*Any?*/value) {
			// summary: query store for default value rather than wait for user to initiate query
			const {_persistFormValues} = this;

			// do nothing here if value will be set as part of Save & New
			if (_persistFormValues?.[f.getUniqueControlName()] != null) {
				return;
			}

			const store = f.m_control.get("store");

			if (store.getDefaultValue && store.getEmptyValue) {
				// get value from widget if not provided
				if (value == null) {
					value = f.m_control.get("value");
				}

				// if value is already valid, then nothing to do
				const bArray = Array.isArray(value);
				if ((bArray && value.length) || (!bArray && value && (value !== store.getEmptyValue()))) {
					return;
				}

				// keep sync if using old aggregate store architecture, but async otherwise;
				// query for aggregate store is always async, and that is what we ideally want, so try using async in such cases;
				// so as to not disrupt other use cases, keep them sync
				const sync = !store.aggregateStoreParams;

				return when(store.getDefaultValue({sync}), (value) => {
					if (bArray) {
						value = (value && (value !== store.getEmptyValue())) ? [value] : [];
					}

					this.onSetDefaultValue(f, store, value);
				});
			}
		},

		_getFieldRelationship: function(/*Field*/f, /*String*/urlNode, /*String?*/value) {
			// summary: return relationship for field - can be overwritten for more advanced logic
			return f.m_relationship;
		},

		_getFieldSelectRootURL: function(field) {
			// summary: get root url for field relationship
			var urlRoot = this.getViewURL();

			// look to parent node for which view was opened if creating new node
			if (!xml.isValidURL(urlRoot)) {
				urlRoot = xml.nodeGetURL(this.defView.m_formData.getParent());
			}

			// if field is dependent on another, then use value of dependent field as root url of store query
			var fieldDep = field.m_dependency ? this.defView.getField(field.m_dependency) : null;
			if (fieldDep) {
				var url = this._getDependencyUrl(fieldDep, field, urlRoot);

				if (!url || (url == csi.EMPTY_URL)) {
					return false;
				} else if (xml.isValidURL(url)) {
					urlRoot = url;
				}
			}

			return urlRoot;
		},

		_getDependencyUrl: function(/*Field*/fieldDep, /*Field*/field, /*String*/urlRoot) {
			// summary: by default, if field1 is dependent on field2, the root URL for the field1 query will be the value of field2
			return fieldDep.m_control ? fieldDep.getValue() : this.getValueForField(fieldDep);
		},

		_getRootRequest: function(/*Node*/node) {
			// summary: query for default/fixed urls in addition to given node and paths
			var req = this.inherited(arguments);
			var urls = req ? [req.getParameter(comm.QUERY_URL)] : [];

			_.each(this._defaultValues, function(v, k) {
				if (xml.isValidURL(v)) {
					urls.push(v);
				}
			});

			_.each(this._fixedValues, function(v, k) {
				if (xml.isValidURL(v)) {
					urls.push(v);
				}
			});

			var urlNodeWithImage = this.urlNodeWithImage;
			if (urlNodeWithImage) {
				urls.push(urlNodeWithImage);
			}

			urls = _.uniq(urls);

			if (!urls.length) {
				return null;
			} else if (req) {
				req.addParameter(comm.QUERY_URL, urls);
			} else {
				req = comm.getNodeRequest(urls, null, true);
			}

			return req;
		},

		/**
		 * Return true if field is displayed and editable
		 * @param {Field} field
		 * @return {boolean}
		 */
		_isFieldActive: function(field) {
			var e = field.m_control;
			return e && !html.isDisabled(e) && html.isVisible(e) && html.isVisible(this.getFieldRow(field));
		},

		_onFadeBody: function() {
			// summary: fade in new body and focus on first field
			if (!this.isValidView()) {
				return;
			}

			var self = this;
			var body = this._resetBodyNode;
			if (body && this.bodyNode.firstChild) {
				domConstruct.place(this.bodyNode, body, "replace");
				html.destroyElement(body);
				this._resetBodyNode = null;

				fx.fadeIn({
					duration: 250,
					node: this.bodyNode,
					onEnd: function() {
						domStyle.set(self.scrollNode, "overflow", "");
						self.publishDomChange();
						self.focusFirstField();
					}
				}).play();
			}
		},

		_setErrorField: function(/*Field?*/field) {
			// summary: focus on field with error, or first editable field if none;
			// delay of 300 seems to work so that form has time to re-adjust based on error message before focusing on widget and showing tooltip -
			// if you do this too quickly the tooltip may not be in the right position after the form moves
			_.delay(function(view) {
				if (view.isValidView()) {
					if (field && view._isFieldActive(field)) {
						field.focusField();
					} else {
						view.focusFirstField();
					}
				}
			}, 300, this);
		},

		/**
		 * Evaluate expression for field, returning the value; if tooltip is passed, then inform tooltip of value
		 * @param {Context} ctx
		 * @param {Field} f
		 * @param {Tooltip} [tooltip]
		 * @return {Any|null}
		 */
		_evaluateExpression: function(ctx, f, tooltip) {
			var expr = this.getFieldExpression(f);
			var x;

			if (!expr && this._valueExpressions) {
				expr = this._valueExpressions[f.getControlName()];
			}

			if (typeof expr === "string") {
				if (ctx.push(f.m_path)) {
					var node = ctx.getNode();

					x = evaluate(expr, node, ctx.cache, false, ctx, {forceLoadMissingNodes: true});

					if (x !== null && x !== undefined && tooltip) {
						tooltip.onFieldExpressionValue(ctx, f, node, x);
					}

					ctx.pop();
				}
			} else if (expr) {
				x = expr();
			}

			return x;
		},

		/**
		 * Reset all aggregate stores based on current field values
		 * @param {Field} [omitField] - if specified, then skip recreating store for it
		 */
		async _resetAggregateStores(omitField) {
			this.defView.m_fields.forEach(async (f) => {
				if ((f !== omitField)) {
					const store = this.getFieldStore(f, f.getValue());
					if (store?.aggregateStoreParams) {
						this._setFieldStore(f, store, true);

						// ensure that counts are up to date when creating a new store for a field that defaults to selecting all or already checked the all option
						if (f.m_control?.defaultAll || f.m_control?.allOptionIsChecked?.()) {
							await store.asyncQuery();
						}
					}
				}
			});
		},

		/**
		 * Disable dependent enum fields if field it depends on has no value
		 */
		_updateDependentEnums() {
			this.defView.m_fields?.forEach((f) => {
				const dependsOn = f.m_rca?.dependsOn;
				const dependsOnField = this.getField(dependsOn);
				if (dependsOnField) {
					const value = dependsOnField.getValue();

					if (!xml.valueIsValid(value, dependsOnField.getType())) {
						this.setFieldDisabled(f, true);
					}
				}
			});
		},

		_updateExpressionFields: function() {
			// summary: evaluate fields containing expressions in the context of current values in the form
			var ctx = this.getFormContext();

			_.each(this.defView.m_fields, function(f) {
				if (!f.bDisableExpressionEvaluation && f.isDefaultValue()) {
					var x = this._evaluateExpression(ctx, f);
					f._expressionResult = x;
					if (x != null) {
						this.setFieldValue(f, x);
						if (f.hasExpression()) {
							f._bUpdated = false;
						}
					}
				}
			}, this);
		},

		/**
		 * Process field rules to see if they should be enabled or disabled
		 */
		_updateFieldEditability: function(field) {
			_.each(this.defView.m_fields, function(f, i) {
				if (f.bCanEdit && f.m_rules && f.m_rules.length) {
					var node = this.getFieldNode(f, false, true);
					var bPass = this._doesFieldPassRules(f, node);
					this.setFieldDisabled(f, !bPass);
				}
			}, this);
		},

		_updateFieldVisibility: function(/*Field?*/field) {
			// summary: processing hidden expressions for fields and update visibility
			if (!this._hideExpressions && !this._requiredExpressions) {
				return false;
			}

			var ctx = this.getFormContext();
			var bChange;

			// now evaluate expressions and update visibility, keeping track if anything changed so that form popup can be resized if necessary
			_.each(this._hideExpressions, function(expr, name) {
				var bHide = evaluate(expr, ctx.getNode(), ctx.cache, false, ctx);

				if (this.setFieldHidden(name, bHide)) {
					bChange = true;
				}
			}, this);

			// now evaluate expressions and update required status of field
			_.each(this._requiredExpressions, function(expr, name) {
				var bRequired = evaluate(expr, ctx.getNode(), ctx.cache, false, ctx);
				this.setFieldRequired(name, bRequired);
			}, this);

			return bChange;
		},

		/**
		 * Update focused field based on changed field and newly focused field and any widgets in between them
		 * @param {Field} field
		 */
		_updateFocusField: function(field) {
			// Do not move focus if user clicks on bolleanMap (checkboxMap)
			if (field.getType() == xml.ATTR_BOOLEANMAP) {
				return;
			}

			var currentFocusNode = focus.curNode;
			if (!currentFocusNode || !this.isValidView()) {
				return;
			}

			var active = {};
			var view = this.getPopupView() || this;	// handle form views in tabs, simple form, or wizard
			var buttons = view.getActionButtons(true);
			var firstButton = _.first(buttons);
			var lastButton = _.last(buttons);
			var n = this.defView.m_fields.length;
			var nFocused;
			var nIndex;

			// find active fields, and indices of current field and focused field
			_.each(this.defView.m_fields, function(f, i) {
				if (this._isFieldActive(f)) {
					active[i] = f;
				}

				if (f == field) {
					nIndex = i;
				}

				if (f.getFocusNode() == currentFocusNode) {
					nFocused = i;
				}
			}, this);

			// a field should never hide or disable itself when changed
			if (nFocused == nIndex) {
				return;
			}

			// if field is not focused, then look for button to know which way focus went (Tab or Shift+Tab)
			if (nFocused == null) {
				if (!firstButton) {
					// can't tell, so do nothing
					return;
				} else if (firstButton.focusNode == currentFocusNode) {
					nFocused = n;
				} else if (lastButton.focusNode == currentFocusNode) {
					nFocused = -1;
				}
			}

			var back = (nFocused < nIndex);
			var end = back ? -1 : n;
			// if focus is now on unavailable field, then move focus to button
			if (firstButton && (nFocused != end) && !active[nFocused]) {
				if (back) {
					lastButton.focus();
				} else {
					firstButton.focus();
				}
			}
		},

		/**
		 * Return true if field should use renderer class to render dynamic control
		 * @param {Field} field
		 * @return {boolean}
		 */
		_useRendererForField(field) {
			const {bUseRenderer, m_typeControl} = field;
			const attrType = field.getType();
			const isStringContainer = xml.attributeTypeGetBase(attrType) === xml.ATTR_STRING;
			const key = this.defView.m_formData?.getKeyContainer();

			// do not render a text field when attribute type is a container and field is text -
			// this case comes from the AttributeForm when a string container type is chosen
			if (isStringContainer &&
				isNullOrUndefined(key) &&
				!types.isLocalizedStringFormat(field.m_format) &&
				((m_typeControl === def.FIELD_TEXT) || (m_typeControl === def.FIELD_TEXTAREA))
			) {
				return false;
			}

			// return true if control type is supported by renderers
			return bUseRenderer || _.contains(CONTROLS_WITH_RENDERER, m_typeControl);
		}
	});
});
